/**
 * `contact` — a contact block (module 22 D3).
 *
 * Copy + address + an optional WhatsApp button. The address links out to a
 * Google Maps search (no embedded third-party iframe on a public page); the
 * WhatsApp button uses the store's own number and simply doesn't render when
 * none is set — the same "drop, don't disable" rule link targets follow.
 * Class-free (see `index.ts`).
 */
import { whatsappHref, type TargetContext } from "./resolve-target.js";
import { SectionHeading } from "./section-heading.js";

export function Contact({
  heading,
  body,
  address,
  showWhatsapp,
  ctx,
}: {
  heading?: string | undefined;
  body?: string | undefined;
  address?: string | undefined;
  showWhatsapp: boolean;
  ctx: TargetContext;
}) {
  const wa = showWhatsapp && ctx.phone ? whatsappHref(ctx.phone) : null;
  const mapHref = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null;

  return (
    <section aria-label={heading ?? "Contact us"} className="flint-contact">
      {heading ? <SectionHeading align="center">{heading}</SectionHeading> : null}
      {body ? (
        <p
          style={{
            fontSize: "0.9375rem",
            lineHeight: 1.6,
            color: "var(--muted)",
            textAlign: "center",
            maxWidth: "48ch",
            marginInline: "auto",
          }}
        >
          {body}
        </p>
      ) : null}
      {address ? (
        <p style={{ marginTop: "1rem", textAlign: "center", fontSize: "0.9375rem", color: "var(--ink)" }}>
          {address}
          {mapHref ? (
            <>
              {" · "}
              <a
                href={mapHref}
                rel="noopener noreferrer"
                target="_blank"
                style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}
              >
                View on map
              </a>
            </>
          ) : null}
        </p>
      ) : null}
      {wa ? (
        <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
          <a
            href={wa}
            rel="noopener noreferrer"
            target="_blank"
            style={{
              display: "inline-flex",
              padding: "0.625rem 1.5rem",
              borderRadius: "var(--r-sm)",
              backgroundColor: "var(--accent)",
              color: "var(--accent-fg)",
              fontWeight: 600,
              fontSize: "0.9375rem",
              textDecoration: "none",
            }}
          >
            Message on WhatsApp
          </a>
        </div>
      ) : null}
    </section>
  );
}
